import React from 'react';
import { Box, Typography, Divider, Grid, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import ClinicHeaderItem from './ClinicHeaderItem';
import { formatDateHour2 } from '../utils/lib';
import ResourcesList from './ResourcesList';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from '../providers/SnackBarProvider';
import { handleError } from '../utils/lib';
import { useTranslation } from 'react-i18next';

function Clinic2Item({clinic2, onDelete, pet, canEdit, config}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { t } = useTranslation();
  const [openConfirm, setOpenConfirm] = React.useState(false);

  let tempWeight = '';
  if (clinic2.temp)
    tempWeight = ` - ${clinic2.temp}${clinic2.temp_unit.name}`;

  if (clinic2.weight)
    tempWeight += ` - ${clinic2.weight}${clinic2.weight_unit.name}`;

  const handleEdit = () => {
    const state = {
      updateClinic: clinic2,
      pet: pet,
    }

    //console.log(state);
    navigate('/main/edit-clinic2', { state: state });
  }

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}pets_clinic_2?id=${clinic2.id}`,
        { withCredentials: true }
      );
      if (onDelete) {
        onDelete(clinic2.id);
      }
    } catch (error) {
      handleError(error, snackbar);
    }
    setOpenConfirm(false);
  };

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const getTitle = () => {
    if (config?.vet?.id === clinic2.vet.id) {
      return clinic2.user.name;
    }
    return `${clinic2.user.name} | ${clinic2.vet.name}`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        //padding: 2, // Esto reemplaza los padding en LinearLayout
      }}
    >        
      <ClinicHeaderItem 
        title={getTitle()}
        subtitle={formatDateHour2(clinic2.date, config) + tempWeight}
        age={clinic2.age}
        thumbUrl={clinic2.user.thumb_url}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canEdit ? handleOpenConfirm : undefined}
      />

      <Box sx={{ marginTop: 2 }}>
        {clinic2.visit_reason && (
          <>
            <Typography variant="caption">
              {t('edit.visitReason')}
            </Typography>
            <Typography>
              {clinic2.visit_reason}
            </Typography>
          </>
        )}

        {clinic2.anamnesis && (
          <>
            <Typography variant="caption">
              {t('edit.anamnesis')}
            </Typography>
            <Typography>
              {clinic2.anamnesis}
            </Typography>
          </>
        )}

        {clinic2.pulse && (
          <>
            <Typography variant="caption">
              {t('edit.pulse')}
            </Typography>
            <Typography>
              {clinic2.pulse}
            </Typography>
          </>
        )}

        {clinic2.respiratory_rate && (
          <>
            <Typography variant="caption">
              {t('edit.respiratoryRate')}
            </Typography>
            <Typography>
              {clinic2.respiratory_rate}
            </Typography>
          </>
        )}

        {clinic2.inspection && (
          <>
            <Typography variant="caption">
              {t('edit.inspection')}
            </Typography>
            <Typography>
              {clinic2.inspection}
            </Typography>
          </>
        )}

        {clinic2.palpation && (
          <>
            <Typography variant="caption">
              {t('edit.palpation')}
            </Typography>
            <Typography>
              {clinic2.palpation}
            </Typography>
          </>
        )}

        {clinic2.auscultation && (
          <>
            <Typography variant="caption">
              {t('edit.auscultation')}
            </Typography>
            <Typography>
              {clinic2.auscultation}
            </Typography>
          </>
        )}

        {clinic2.helper_methods && (
          <>
            <Typography variant="caption">
              {t('edit.auxiliaryMethods')}
            </Typography>
            <Typography>
              {clinic2.helper_methods}
            </Typography>
          </>
        )}

        {clinic2.presumptive_diagnostic && (
          <>
            <Typography variant="caption">
              {t('edit.presumptiveDiagnosis')}
            </Typography>
            <Typography>
              {clinic2.presumptive_diagnostic}
            </Typography>
          </>
        )}

        {clinic2.symptoms?.length > 0 && (
          <>
            <Typography variant="caption">
              {t('edit.symptoms')}
            </Typography>
            {clinic2.symptoms.map((symptom, index) => (
              <Typography key={index}>
                {symptom.name}
              </Typography>
            ))}
          </>
        )}

        {clinic2.diagnostic && (
          <>
            <Typography variant="caption">
              {t('edit.diagnosis')}
            </Typography>
            <Typography>
              {clinic2.diagnostic.name}
            </Typography>
          </>
        )}

        {clinic2.treatment && (
          <>
            <Typography variant="caption">
              {t('common.treatment')}
            </Typography>
            <Typography>
              {clinic2.treatment.name}
            </Typography>
          </>
        )}
      </Box>

      {clinic2.resources?.length > 0 && (
        <ResourcesList 
          resources={clinic2.resources}
        />
      )}

      {/* Línea divisoria */}
      <Divider
        id="div_line"
        sx={{
          marginTop: 3,
          marginBottom: 1,
          marginLeft: 0,
          marginRight: 0,
          backgroundColor: '#dadada',
          height: 1,
        }}
      />
      <Dialog
        open={openConfirm}
        onClose={handleCloseConfirm}
      >
        <DialogTitle>{t('common.confirmDeletion')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('layouts.confirmDeleteConsult')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Clinic2Item;
